// Single source of truth for sidebar / mobile nav definitions.
// Both Sidebar and MobileNav read from here so role + feature-status
// filtering produce the same set of items, just laid out differently.
//
// `module` — gates visibility by role (ROLE_MODULES in App.js).
// `permKey` — optional. When the user is Admin (isAdmin: true), the matching
//   `OpsUserPermission.module` toggle in TeamSettings can hide the item even
//   if the role allows it. Owners ignore the toggle. Non-admin staff don't
//   see toggles in the UI — their access stays purely role-based.

export const SCHEDULE_ITEM = { key: 'clients', label: 'Schedule', icon: '📅', module: 'schedule' };

export const NAV_SECTIONS = [
  {
    title: 'TODAY',
    items: [
      { key: 'inbox',     label: 'Inbox',            icon: '📥', module: 'performance', moduleKey: 'inbox', permKey: 'inbox' },
      { key: 'tasks',     label: 'Tasks',            icon: '📋', module: 'tasks' },
      // Visible to everyone (rides the universal `tasks` module) — anyone can
      // see/complete their own tasks; managing is gated server-side by the
      // `task_calendar` Team Access toggle.
      { key: 'task_calendar', label: 'Task Calendar', icon: '🗓️', module: 'tasks' },
      { key: 'audits',    label: 'Morning Routine',  icon: '🔍', module: 'performance', moduleKey: 'audits', permKey: 'audits' },
      { key: 'optimizer', label: 'Optimizer',        icon: '⚡', module: 'optimizer', permKey: 'optimizer' },
      { key: 'calloff',   label: 'Call-Off Advisor', icon: '📞', module: 'calloff', permKey: 'call_offs' },
      { key: 'late_starts', label: 'Staff Advisor',    icon: '🌅', module: 'late_starts' },
      { key: 'reporting', label: 'Reporting',        icon: '📝', module: 'reporting' },
      // `workspace: 'poppy'` items live in the Poppy workspace on desktop
      // (Sidebar brand switcher). `shortLabel` drops the redundant "Poppy"
      // prefix inside that workspace. MobileNav keeps the full labels.
      { key: 'poppy_reports', label: 'Poppy Reports', shortLabel: 'Reports', icon: '🌸', module: 'poppy_reports', workspace: 'poppy' },
      { key: 'poppy_book', label: 'Poppy Book', shortLabel: 'Book', icon: '📖', module: 'poppy_book', workspace: 'poppy' },
      { key: 'poppy_master_data', label: 'Master Data', shortLabel: 'Master Data', icon: '🗂️', module: 'poppy_master_data', workspace: 'poppy' },
    ],
  },
  {
    title: 'PEOPLE',
    items: [
      { key: 'hr',          label: 'Staff',        icon: '👥', module: 'hr' },
      { key: 'performance', label: 'Performance',  icon: '📊', module: 'performance', permKey: 'performance' },
      // Owner-only in v1: 'booking_order' isn't in any ROLE_MODULES list, and
      // canModule() always passes for owner. Toggleable later via permKey.
      { key: 'booking_order', label: 'Booking Order', icon: '📇', module: 'booking_order', permKey: 'booking_order' },
      // The change-requests shadow log + desk-device/config admin (Task 13).
      // Gated on 'change_requests_log' (owner-only default, per-person Team
      // toggle) — NOT on 'change_requests', which is the provider send toggle
      // (✎ Modify on the Schedule card) and must not reveal the location-wide
      // log. The settings-gated device/config sub-sections inside the page
      // check canAccessSettings separately.
      { key: 'change_requests', label: 'Change Requests', icon: '🔀', module: 'change_requests_log' },
      // Gated on 'schedule' so exactly the clock-in population sees it (same
      // rule as the login clock gate). Lands in the mobile More menu.
      { key: 'time_clock',  label: 'Time Clock',   icon: '🕐', module: 'schedule' },
      { key: 'me',          label: 'My Dashboard', icon: '👤', module: 'me' },
    ],
  },
  {
    title: 'GUESTS',
    items: [
      { key: 'guests',         label: 'Complaints',     icon: '💬', module: 'guests', permKey: 'guests' },
      { key: 'google_reviews', label: 'Google Reviews', icon: '⭐', module: 'analytics', moduleKey: 'google_reviews', permKey: 'google_reviews' },
      { key: 'service_logs',   label: 'Service Logs',   icon: '🧾', module: 'guests', moduleKey: 'service_logs', permKey: 'service_logs' },
    ],
  },
  {
    title: 'ANALYTICS',
    items: [
      { key: 'analytics', label: 'Overview',  icon: '📊', module: 'analytics' },
      { key: 'kiosk',     label: 'Kiosk',     icon: '📈', module: 'kiosk' },
      { key: 'campaigns', label: 'Campaigns', icon: '📣', module: 'campaigns' },
    ],
  },
  {
    title: 'OPERATIONS',
    items: [
      { key: 'payroll',   label: 'Payroll',   icon: '💰', module: 'hr' },
      { key: 'month-end', label: 'Month End', icon: '📋', module: 'month_end', permKey: 'month_end' },
      { key: 'inventory', label: 'Inventory', icon: '📦', module: 'inventory', permKey: 'inventory' },
      { key: 'settings',  label: 'Settings',  icon: '⚙️', module: 'settings' },
    ],
  },
];

// Flat list including Schedule — used by MobileNav to filter and order.
export const ALL_NAV_ITEMS = [
  SCHEDULE_ITEM,
  ...NAV_SECTIONS.flatMap(s => s.items),
];

const poppyItem = (key) => ALL_NAV_ITEMS.find(i => i.key === key);

// Poppy workspace sidebar, sectioned by product area (mirrors the ops
// sections). `stub: true` items are unbuilt Zenoti-replacement phases with
// NO view behind them — the Sidebar renders them coming-soon for everyone
// (owner included) and never navigates to them; they're also flagged in
// FEATURE_STATUS so View As follows the standard coming-soon path. Their
// module keys aren't in any ROLE_MODULES list yet, so — same pattern as
// booking_order — canModule()'s owner bypass makes them owner-only until
// the real modules/toggles are wired up. Stubs are deliberately NOT in
// ALL_NAV_ITEMS, so MobileNav never shows them.
//
// poppy_configuration is a REAL view (PoppyConfiguration.jsx, the §16
// admin-IA hub) defined inline like the stubs — desktop Poppy workspace
// only, deliberately kept out of ALL_NAV_ITEMS/MobileNav. Access is the
// poppy_configuration module, management-gated like poppy_master_data
// (backend/lib/ops-modules.js MANAGEMENT defaults + Team-Access toggle).
export const POPPY_NAV_SECTIONS = [
  { title: 'APPOINTMENTS',  items: [poppyItem('poppy_book')] },
  // poppy_guests is a REAL view (PoppyGuestProfile.jsx — search + read-only
  // guest profile) defined inline like poppy_configuration: desktop Poppy
  // workspace only, deliberately kept out of ALL_NAV_ITEMS/MobileNav.
  // Access is the poppy_guests module, management-gated like
  // poppy_master_data (backend/lib/ops-modules.js MANAGEMENT defaults +
  // Team-Access toggle).
  { title: 'GUESTS',        items: [{ key: 'poppy_guests', label: 'Guests', icon: '👥', module: 'poppy_guests', workspace: 'poppy' }] },
  { title: 'POINT OF SALE', items: [{ key: 'poppy_pos', label: 'Register', icon: '💳', module: 'poppy_pos', workspace: 'poppy', stub: true }] },
  { title: 'ADMIN',         items: [
    poppyItem('poppy_master_data'),
    // Blockout-type master list + shadow-engine divergence review — REAL views
    // (PoppyBlockoutTypes.jsx / PoppyDivergenceReview.jsx), desktop Poppy
    // workspace only, defined inline like poppy_configuration. Management-gated
    // by their modules (backend/lib/ops-modules.js MANAGEMENT defaults +
    // Team-Access toggle); canModule()'s owner bypass shows them to the owner.
    { key: 'poppy_blockouts', label: 'Blockout Types', icon: '🚫', module: 'poppy_blockouts', workspace: 'poppy' },
    { key: 'poppy_configuration', label: 'Configuration', icon: '⚙️', module: 'poppy_configuration', workspace: 'poppy' },
    { key: 'poppy_divergence', label: 'Divergence Review', icon: '⚖️', module: 'poppy_divergence', workspace: 'poppy' },
    // Service Clearances has NO sidebar item — entered via Configuration →
    // Business → Employee (Lindsay 2026-08-07), same pattern as Business
    // details. The LazyTab + service_clearance module toggle still exist.
  ] },
  { title: 'REPORTS',       items: [poppyItem('poppy_reports')] },
];

export const POPPY_NAV_ITEMS = POPPY_NAV_SECTIONS.flatMap(s => s.items);
export const POPPY_NAV_KEYS = POPPY_NAV_ITEMS.map(i => i.key);

// Mobile bottom-bar primary slot priority. Top N visible items for the
// current role become primary tabs; the rest go to More. Ordered to put
// staff essentials (Schedule / Tasks / My Dashboard) first, then admin
// daily drivers, then everything else.
export const MOBILE_PRIORITY = [
  'clients', 'tasks', 'me',
  'time_clock',
  'task_calendar',
  'performance', 'guests', 'kiosk',
  'inbox', 'reporting', 'inventory',
  'hr', 'analytics', 'audits',
  'calloff', 'optimizer', 'campaigns',
  'google_reviews', 'service_logs', 'payroll', 'sync',
  'booking_order', 'month-end', 'settings',
];

export const MAX_PRIMARY_TABS = 3;
